// src/pages/portal/Support.js
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { collection, query, where, orderBy, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";

export default function Support() {
  const { currentUser } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "", priority: "normal", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  async function loadTickets() {
    if (!currentUser) return;
    const snap = await getDocs(query(collection(db, "tickets"), where("uid", "==", currentUser.uid), orderBy("createdAt", "desc")));
    setTickets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  useEffect(() => { loadTickets(); }, [currentUser]);

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    await addDoc(collection(db, "tickets"), {
      ...form,
      uid: currentUser.uid,
      email: currentUser.email,
      status: "open",
      createdAt: serverTimestamp(),
    });
    setSuccess(true);
    setShowNew(false);
    setForm({ subject: "", category: "", priority: "normal", message: "" });
    await loadTickets();
    setSubmitting(false);
    setTimeout(() => setSuccess(false), 4000);
  };

  const STATUS = { open: { label: "Open", cls: "badge-amber" }, "in-progress": { label: "In Progress", cls: "badge-blue" }, resolved: { label: "Resolved", cls: "badge-accent" } };
  const PRIORITY = { low: "#94a3b8", normal: "#60a5fa", high: "#f87171", urgent: "#f87171" };

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
        <div>
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700, fontFamily: "var(--font-head)", marginBottom: 4 }}>Support Tickets</h1>
          <p style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>Get help with your services. We respond within 1 business day.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ New Ticket</button>
      </motion.div>

      {success && <div className="alert alert-success">✓ Ticket submitted! We'll respond shortly.</div>}

      {/* New ticket form */}
      {showNew && (
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} style={{ background: "var(--surface)", border: "1px solid var(--border-med)", borderRadius: 16, padding: 28, marginBottom: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h2 style={{ fontSize: "1rem", fontWeight: 600 }}>New Support Ticket</h2>
            <button onClick={() => setShowNew(false)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 18 }}>✕</button>
          </div>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Category *</label>
                <select className="form-select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} required>
                  <option value="">Select category</option>
                  <option value="bug">Bug / Error</option>
                  <option value="update">Content Update Request</option>
                  <option value="billing">Billing Question</option>
                  <option value="feature">Feature Request</option>
                  <option value="renewal">Subscription Renewal</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Priority</label>
                <select className="form-select" value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Subject *</label>
              <input className="form-input" placeholder="Brief description of your issue" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Message *</label>
              <textarea className="form-textarea" placeholder="Describe your issue in detail. The more context you provide, the faster we can help." value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} required style={{ minHeight: 120 }} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? "Submitting..." : "Submit Ticket"}</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowNew(false)}>Cancel</button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Tickets list */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 48 }}><div className="spinner" style={{ margin: "0 auto" }} /></div>
      ) : tickets.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 24px", background: "var(--surface)", borderRadius: 16, border: "1px dashed var(--border-med)" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🎫</div>
          <h3 style={{ fontWeight: 600, marginBottom: 8 }}>No tickets yet</h3>
          <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginBottom: 20 }}>If you need help with anything, submit a ticket and we'll be right on it.</p>
          <button className="btn btn-primary" onClick={() => setShowNew(true)}>Submit Your First Ticket</button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {tickets.map((t, i) => (
            <motion.div key={t.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "18px 20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.9375rem", marginBottom: 4 }}>{t.subject}</div>
                    <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", display: "flex", gap: 12, flexWrap: "wrap" }}>
                      <span>📂 {t.category || "General"}</span>
                      <span style={{ color: PRIORITY[t.priority] || "var(--text-muted)" }}>● {t.priority || "normal"} priority</span>
                      <span>{t.createdAt?.toDate?.()?.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" }) || "—"}</span>
                    </div>
                  </div>
                  <span className={`badge ${STATUS[t.status]?.cls || "badge-amber"}`}>{STATUS[t.status]?.label || "Open"}</span>
                </div>
                {t.message && (
                  <div style={{ marginTop: 12, padding: "12px 14px", background: "var(--navy-mid)", borderRadius: 8, fontSize: "0.8125rem", color: "var(--text-muted)", lineHeight: 1.5 }}>
                    {t.message.length > 200 ? t.message.slice(0, 200) + "..." : t.message}
                  </div>
                )}
                {t.adminReply && (
                  <div style={{ marginTop: 8, padding: "12px 14px", background: "rgba(0,201,167,0.06)", border: "1px solid rgba(0,201,167,0.15)", borderRadius: 8, fontSize: "0.8125rem", color: "var(--text-muted)", lineHeight: 1.5 }}>
                    <span style={{ color: "var(--accent)", fontWeight: 600 }}>Aimble Support: </span>{t.adminReply}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
